import React, { useState } from "react";
import ActivityHeader from "./ActivityHeader";
import ActivityContent from "./ActivityContent";
import ReactionButtons from "../actions/ReactionButtons";
import ReactionHistory from "../actions/ReactionHistory";
import CommentHistory from "../actions/CommentHistory";

const ActivityCard = ({
  activity,
  isSelected = false,
  isExpanded: controlledIsExpanded,
  onExpandToggle: controlledOnExpandToggle,
  onSelectToggle,
  onReaction,
  onGradeClick,
  onImageClick,
  onDocView,
  processing = false,
  currentUserEmail,
  fromSource = "Professor",
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
  const onExpandToggle = controlledOnExpandToggle || (() => setInternalIsExpanded((prev) => !prev));

  const isHodView = fromSource === "HOD";
  const isPrincipalView = fromSource === "Principal" || fromSource === "ReadOnly";

  const facultyReactions = (activity?.reactions || []).filter((r) => r.from_source !== "HOD");
  const facultyComments = (activity?.comments || []).filter((c) => c.from_source !== "HOD");
  const hodReactions = (activity?.reactions || []).filter((r) => r.from_source === "HOD");
  const hodComments = (activity?.comments || []).filter((c) => c.from_source === "HOD");

  return (
    <div
      className={`bg-white rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${
        isSelected ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/20" : "border-slate-200"
      }`}
    >
      {/* Header (Title, Date, Type, Attempt, Status, Expand Arrow) */}
      <ActivityHeader
        activity={activity}
        isSelected={isSelected}
        onSelectToggle={onSelectToggle}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
        fromSource={fromSource}
      />

      {/* Expanded Content (Description, Drawings, Images, Documents, Assessment Actions, History) */}
      {isExpanded && (
        <div className="mt-4 space-y-4 pt-3 border-t border-slate-200/80">
          {/* Detailed Content */}
          <ActivityContent
            activity={activity}
            onImageClick={onImageClick}
            onDocView={onDocView}
          />

          {isPrincipalView ? (
            /* ──────────────────────────────────────────────────────────
               PRINCIPAL DASHBOARD VIEW: STRICTLY READ-ONLY ACCESS
               1. Read-Only Faculty Reactions & Feedback Section
               2. Read-Only HOD Reaction & Feedback Section
               ────────────────────────────────────────────────────────── */
            <div className="space-y-4 pt-2">
              {/* 1. FACULTY REACTION SECTION */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <span>👨‍🏫 Faculty Member Evaluation & Reaction</span>
                  </h4>
                  {facultyReactions.length === 0 && (
                    <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                      ⏳ Pending Faculty Evaluation
                    </span>
                  )}
                </div>

                {facultyReactions.length > 0 || facultyComments.length > 0 ? (
                  <>
                    <ReactionHistory reactions={facultyReactions} />
                    <CommentHistory comments={facultyComments} />
                  </>
                ) : (
                  <p className="text-xs text-slate-500 italic py-1">
                    No faculty member (Professor, Assistant/Associate Professor, Senior Resident) has evaluated this activity yet.
                  </p>
                )}
              </div>

              {/* 2. HOD REACTION SECTION */}
              <div className="p-4 rounded-xl bg-purple-50/60 border border-purple-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider flex items-center gap-2">
                    <span>🏛️ Head of Department (HOD) Evaluation & Reaction</span>
                  </h4>
                  {hodReactions.length === 0 && (
                    <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                      ⏳ Pending HOD Evaluation
                    </span>
                  )}
                </div>

                {hodReactions.length > 0 || hodComments.length > 0 ? (
                  <>
                    <ReactionHistory reactions={hodReactions} />
                    <CommentHistory comments={hodComments} />
                  </>
                ) : (
                  <p className="text-xs text-slate-500 italic py-1">
                    The Head of Department has not provided an evaluation for this activity yet.
                  </p>
                )}
              </div>
            </div>
          ) : isHodView ? (
            /* ──────────────────────────────────────────────────────────
               HOD DASHBOARD VIEW:
               1. Faculty Reactions & Feedback Section
               2. Independent HOD Reaction & Assessment Section
               ────────────────────────────────────────────────────────── */
            <div className="space-y-4 pt-2">
              {/* 1. FACULTY REACTION SECTION */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <span>👨‍🏫 Faculty Member Evaluations & Feedback</span>
                  </h4>
                  {facultyReactions.length === 0 && (
                    <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                      ⏳ Pending Faculty Evaluation
                    </span>
                  )}
                </div>

                {facultyReactions.length > 0 || facultyComments.length > 0 ? (
                  <>
                    <ReactionHistory reactions={facultyReactions} />
                    <CommentHistory comments={facultyComments} />
                  </>
                ) : (
                  <p className="text-xs text-slate-500 italic py-1">
                    No faculty member (Professor, Assistant/Associate Professor, Senior Resident) has evaluated this activity yet.
                  </p>
                )}
              </div>

              {/* 2. INDEPENDENT HOD REACTION SECTION */}
              <div className="p-4 rounded-xl bg-purple-50/60 border border-purple-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider flex items-center gap-2">
                    <span>🏛️ Head of Department (HOD) Assessment & Action</span>
                    <span className="px-2 py-0.5 rounded-full bg-purple-200 text-purple-800 font-bold text-[10px]">HOD Portal</span>
                  </h4>
                </div>

                {/* HOD Action Buttons (HOD Approve / HOD Reject / Edit HOD Reaction) */}
                <div className="w-full">
                  <ReactionButtons
                    activity={activity}
                    onReaction={(act, dec) => onReaction(act, dec, "HOD")}
                    onGradeClick={(act, dec) => onGradeClick(act, dec, "HOD")}
                    processing={processing}
                    currentUserEmail={currentUserEmail}
                    fromSource="HOD"
                  />
                </div>

                {/* HOD Comment / Feedback History */}
                {hodComments.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-purple-100">
                    <CommentHistory comments={hodComments} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ──────────────────────────────────────────────────────────
               FACULTY DASHBOARD VIEW:
               1. Faculty Reaction Bar (Approve / Reject / Edit)
               2. Faculty Reactions & Comments History
               3. HOD Verification (Read-Only Card below if present)
               ────────────────────────────────────────────────────────── */
            <div className="space-y-4 pt-2">
              {/* Assessment Action Bar for Faculty */}
              <div className="pt-3 border-t border-slate-100 w-full">
                <ReactionButtons
                  activity={activity}
                  onReaction={(act, dec) => onReaction(act, dec, "Professor")}
                  onGradeClick={(act, dec) => onGradeClick(act, dec, "Professor")}
                  processing={processing}
                  currentUserEmail={currentUserEmail}
                  fromSource="Professor"
                />
              </div>

              {/* Reaction & Comment History for Faculty */}
              <ReactionHistory reactions={facultyReactions} />
              <CommentHistory comments={facultyComments} />

              {/* HOD Verification Read-Only Display for Faculty */}
              {hodReactions.length > 0 && (
                <div className="p-3.5 rounded-xl bg-purple-50 border border-purple-200 text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-purple-900 flex items-center gap-1.5">
                      🏛️ Head of Department (HOD) Verification
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full bg-purple-200 text-purple-800 text-[10px] font-bold">
                      {hodReactions[0]?.decision || (hodReactions[0]?.type === '✅' ? 'Approved' : 'Rejected')}
                    </span>
                  </div>
                  <ReactionHistory reactions={hodReactions} />
                  {hodComments.length > 0 && <CommentHistory comments={hodComments} />}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
